import { useContext, useEffect, useRef } from 'react';
import OLVectorLayer from 'ol/layer/Vector';
import { Vector } from 'ol/source';
import * as olInteraction from 'ol/interaction';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import VectorSource from 'ol/source/Vector';
import PropTypes from 'prop-types';
import Point from 'ol/geom/Point';
import { fromLonLat, toLonLat } from 'ol/proj';
import GeoMapContext from '../../lib/geo-map/GeoMapContext';
// eslint-disable-next-line boundaries/element-types,import/no-cycle
import { findCenter } from '../../../widgets/vehicles/VehicleRouteFormWidget';
import { addHoleToPolygonGeometry, vectorStyle } from './utils';
import vectorStyleRoutePointNumberInside from './constants';
import { normalizePolygonCoordinates } from '../../geo-map/lib';

let modify = null;
let draw = null;

function PolygonVectorLayer(props) {
  const {
    id,
    zIndex,
    isCreatePolygon,
    coordinates,
    setCoordsGeoZone,
    text,
    isChangeable,
    isSelectable,
    strokeColor,
    fillColor,
    strokeWidth,
    numeraryText,
    numerary,
    disableAutoZoom,
    isCenterText,
  } = props;

  const { map } = useContext(GeoMapContext);
  const holeDrawRef = useRef(null);
  const keyHandlersRef = useRef({ down: null, up: null });

  const normalizedCoordinates = normalizePolygonCoordinates(coordinates);

  useEffect(() => {
    if (!map) return undefined;

    let vectorSource = new Vector({ projection: map.getView().projection });
    let markerSource = new Vector({ projection: map.getView().projection });
    let polygonFeature = null;

    if (isCreatePolygon) {
      // пустой слой для нового полигона
      vectorSource = new Vector({ projection: map.getView().projection });
    } else if (normalizedCoordinates?.length) {
      // отображение существующего полигона
      const center = findCenter(
        normalizedCoordinates[0].map((e) => {
          const cords = toLonLat(e);
          return { lat: cords[1], lon: cords[0] };
        }),
      );

      polygonFeature = new Feature({ geometry: new Polygon(normalizedCoordinates) });
      polygonFeature.set('zoneId', id);
      vectorSource = new VectorSource({ features: [polygonFeature] });

      if (numerary) {
        const markerFeature = new Feature({
          geometry: new Point(fromLonLat([center.lon, center.lat])),
          tooltipText: '',
        });
        markerSource = new VectorSource({ features: [markerFeature] });
      }

      const extent = polygonFeature.getGeometry().getExtent();
      if (!disableAutoZoom) map.getView().fit(extent, { padding: [100, 100, 100, 100] });
    }

    const layerPoly = new OLVectorLayer({
      source: vectorSource,
      style: (feature) =>
        vectorStyle({
          strokeColor,
          fillColor,
          strokeWidth,
          label: text || feature.get('name'),
          isCenterText,
        }),
    });
    map.addLayer(layerPoly);
    layerPoly.setZIndex(zIndex);

    let layerMarker;
    if (numerary && !isCreatePolygon) {
      layerMarker = new OLVectorLayer({
        source: markerSource,
        style: () => {
          const style = vectorStyleRoutePointNumberInside;
          style.getText().setText(numeraryText);
          return style;
        },
      });
      map.addLayer(layerMarker);
      layerMarker.setZIndex(zIndex + 1);
    }

    const select = new olInteraction.Select({ layers: [layerPoly] });
    if (isSelectable || isChangeable) map.addInteraction(select);

    const enableModify = () => {
      if (!isChangeable) return;
      modify = new olInteraction.Modify({ features: select.getFeatures() });
      map.addInteraction(modify);

      modify.on('modifyend', () => {
        const feat = select.getFeatures().getArray()[0];
        if (!feat) return;
        const coords = feat.getGeometry().getCoordinates();
        if (setCoordsGeoZone) setCoordsGeoZone(coords);
      });
    };
    enableModify();

    // Рисование нового полигона
    if (isCreatePolygon) {
      draw = new olInteraction.Draw({ type: 'Polygon', stopClick: true, source: vectorSource });
      map.addInteraction(draw);

      draw.on('drawend', (e) => {
        const newCoords = e.feature.getGeometry().getCoordinates();
        if (setCoordsGeoZone) setCoordsGeoZone(newCoords);
        map.removeInteraction(draw);
        draw = null;
      });
    }

    // Дырки в полигонах
    const startHoleMode = () => {
      const selected = select.getFeatures().getLength() > 0 ? select.getFeatures().item(0) : polygonFeature;
      if (!selected || selected.getGeometry().getType() !== 'Polygon') return;

      // Чтобы не было конфликтов, вызывающих баги
      if (modify) map.removeInteraction(modify);

      const drawHole = new olInteraction.Draw({ type: 'Polygon', stopClick: true, source: null });
      holeDrawRef.current = drawHole;

      drawHole.on('drawend', (e) => {
        const holeCoords = e.feature.getGeometry().getCoordinates()[0];
        const ok = addHoleToPolygonGeometry(selected.getGeometry(), holeCoords);

        if (isChangeable && !modify) enableModify();
        map.removeInteraction(drawHole);
        holeDrawRef.current = null;

        if (ok && setCoordsGeoZone) setCoordsGeoZone(selected.getGeometry().getCoordinates());
      });

      map.addInteraction(drawHole);
    };

    const stopHoleMode = () => {
      if (holeDrawRef.current) {
        map.removeInteraction(holeDrawRef.current);
        holeDrawRef.current = null;
      }
      if (isChangeable) enableModify();
    };

    const onKeyDown = (evt) => {
      if (evt.code === 'KeyZ' && !holeDrawRef.current) startHoleMode();
    };
    const onKeyUp = (evt) => {
      if (evt.code === 'KeyZ') stopHoleMode();
    };

    keyHandlersRef.current.down = onKeyDown;
    keyHandlersRef.current.up = onKeyUp;
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      if (map) {
        if (holeDrawRef.current) map.removeInteraction(holeDrawRef.current);
        if (draw) map.removeInteraction(draw);
        map.removeLayer(layerPoly);
        if (layerMarker) map.removeLayer(layerMarker);
        if (modify) map.removeInteraction(modify);
        if (isSelectable || isChangeable) map.removeInteraction(select);
      }
    };
  }, [
    map,
    coordinates,
    isCreatePolygon,
    zIndex,
    setCoordsGeoZone,
    text,
    strokeColor,
    fillColor,
    strokeWidth,
    isSelectable,
    isChangeable,
    numerary,
    numeraryText,
  ]);

  return null;
}

PolygonVectorLayer.propTypes = {
  id: PropTypes.number,
  zIndex: PropTypes.number,
  isCreatePolygon: PropTypes.bool,
  coordinates: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))),
  setCoordsGeoZone: PropTypes.func,
  text: PropTypes.string,
  isChangeable: PropTypes.bool,
  isSelectable: PropTypes.bool,
  strokeColor: PropTypes.string,
  fillColor: PropTypes.string,
  strokeWidth: PropTypes.number,
  numeraryText: PropTypes.string,
  numerary: PropTypes.bool,
  disableAutoZoom: PropTypes.bool,
  isCenterText: PropTypes.bool,
};

PolygonVectorLayer.defaultProps = {
  id: crypto.randomUUID(),
  zIndex: 0,
  coordinates: [],
  setCoordsGeoZone: null,
  text: '',
  isChangeable: false,
  isCreatePolygon: false,
  isSelectable: false,
  strokeColor: '#0e97fa',
  strokeWidth: 4,
  fillColor: 'rgba(0, 153, 255, 0.2)',
  numeraryText: null,
  numerary: null,
  disableAutoZoom: false,
  isCenterText: true,
};

export default PolygonVectorLayer;
